import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { motion, AnimatePresence } from 'framer-motion';
import Badge from '../ui/Badge';

// Configure PDF.js worker for react-pdf v10+
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PDFViewer Component
 * Secure PDF viewer with download prevention, fullscreen mode, and thumbnails
 */

// Helper to determine if URL is external (different origin)
const isExternalUrl = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin !== window.location.origin;
  } catch {
    return false;
  }
};

const PDFViewer = ({
  fileUrl,
  fileName = 'Document',
  onPageChange,
  onZoomChange,
  showThumbnails = true,
  enableFullscreen = true,
  watermarkText = '',
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showThumbnailSidebar, setShowThumbnailSidebar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [containerWidth, setContainerWidth] = useState(800);
  const containerRef = useRef(null);
  const documentAreaRef = useRef(null);
  const pageRefs = useRef({});

  // Prepare PDF file source with CORS handling for external URLs
  const pdfFile = useMemo(() => {
    if (!fileUrl) return null;

    // For external URLs (Cloudinary, S3, etc.), use object format with CORS settings
    if (isExternalUrl(fileUrl)) {
      return {
        url: fileUrl,
        withCredentials: false,
        // Add cache-busting for retries
        ...(retryCount > 0 && { cMapUrl: `${fileUrl}?retry=${retryCount}` }),
      };
    }

    // For same-origin URLs, use string format
    return fileUrl;
  }, [fileUrl, retryCount]);

  // Measure container width for responsive PDF sizing
  useEffect(() => {
    const measureWidth = () => {
      if (documentAreaRef.current) {
        const width = documentAreaRef.current.clientWidth - 32; // subtract padding
        setContainerWidth(Math.min(Math.max(width, 300), 800)); // clamp between 300-800px
      }
    };

    measureWidth();
    window.addEventListener('resize', measureWidth);
    return () => window.removeEventListener('resize', measureWidth);
  }, []);

  // Add loading timeout - if PDF doesn't load within 15 seconds, show error
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading && !error) {
        setError('PDF is taking too long to load. The file may not exist or there may be a network issue.');
        setLoading(false);
      }
    }, 15000);

    return () => clearTimeout(timer);
  }, [loading, error, retryCount]);

  // Document loaded callback
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (err) => {
    console.error('Error loading PDF:', err);

    // Provide more specific error messages
    let errorMessage = 'Failed to load PDF document';
    const errMsg = err?.message || String(err) || '';
    const isExternal = isExternalUrl(fileUrl);

    if (errMsg.includes('CORS') || errMsg.includes('cross-origin') || errMsg.includes('Failed to fetch')) {
      errorMessage = isExternal
        ? 'This PDF is hosted externally and cannot be displayed in the viewer due to security restrictions. Please use "Open in New Tab" to view it.'
        : 'PDF cannot be loaded due to cross-origin restrictions. Try opening in a new tab.';
    } else if (errMsg.includes('network') || errMsg.includes('fetch') || errMsg.includes('NetworkError')) {
      errorMessage = isExternal
        ? 'Unable to load this external PDF. Please try "Open in New Tab" to view it directly.'
        : 'Network error while loading PDF. Please check your connection.';
    } else if (errMsg.includes('Invalid PDF') || errMsg.includes('corrupted')) {
      errorMessage = 'The PDF file appears to be invalid or corrupted.';
    } else if (isExternal) {
      // For any other error with external URLs, suggest opening in new tab
      errorMessage = 'Unable to display this PDF in the viewer. Please use "Open in New Tab" to view it.';
    }

    setError(errorMessage);
    setLoading(false);
  };

  // Handle page change — in continuous scroll mode this scrolls to the target
  // page. currentPage is kept in sync by the IntersectionObserver below.
  const handlePageChange = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= numPages) {
      setCurrentPage(pageNumber);

      if (onPageChange) {
        onPageChange(pageNumber);
      }

      if (pageRefs.current[pageNumber]) {
        pageRefs.current[pageNumber].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [numPages, onPageChange]);

  // Track which page is currently most visible as user scrolls. Uses
  // IntersectionObserver on each rendered page wrapper so the bottom-bar
  // indicator stays in sync without any JS scroll-event math.
  useEffect(() => {
    if (!numPages) return undefined;

    const root = documentAreaRef.current;
    if (!root) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry most visible in viewport
        let bestPage = null;
        let bestRatio = 0;
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestPage = parseInt(entry.target.dataset.pageNumber, 10);
          }
        }
        if (bestPage) {
          setCurrentPage((prev) => (prev === bestPage ? prev : bestPage));
          if (onPageChange) onPageChange(bestPage);
        }
      },
      { root, threshold: [0.25, 0.5, 0.75] }
    );

    // Observe every page wrapper
    Object.values(pageRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [numPages, onPageChange]);

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    const newScale = Math.min(scale + 0.25, 3.0);
    setScale(newScale);
    if (onZoomChange) onZoomChange(newScale);
  }, [scale, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    const newScale = Math.max(scale - 0.25, 0.5);
    setScale(newScale);
    if (onZoomChange) onZoomChange(newScale);
  }, [scale, onZoomChange]);

  const handleResetZoom = useCallback(() => {
    setScale(1.0);
    if (onZoomChange) onZoomChange(1.0);
  }, [onZoomChange]);

  const handleFitToWidth = () => {
    setScale(1.5);
    if (onZoomChange) onZoomChange(1.5);
  };

  // Fullscreen functionality
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Disable Ctrl+S, Ctrl+P (save and print)
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) {
        e.preventDefault();
        return false;
      }

      // Navigation shortcuts
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        handlePageChange(currentPage + 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        handlePageChange(currentPage - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        handlePageChange(1);
      } else if (e.key === 'End') {
        e.preventDefault();
        handlePageChange(numPages);
      }

      // Zoom shortcuts
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          handleZoomIn();
        } else if (e.key === '-') {
          e.preventDefault();
          handleZoomOut();
        } else if (e.key === '0') {
          e.preventDefault();
          handleResetZoom();
        }
      }

      // Fullscreen toggle
      if (e.key === 'f' && !e.ctrlKey && !e.metaKey && enableFullscreen) {
        e.preventDefault();
        toggleFullscreen();
      }

      // Toggle thumbnails
      if (e.key === 't' && !e.ctrlKey && !e.metaKey && showThumbnails) {
        e.preventDefault();
        setShowThumbnailSidebar(!showThumbnailSidebar);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, numPages, scale, showThumbnailSidebar, showThumbnails, enableFullscreen, handlePageChange, handleZoomIn, handleZoomOut, handleResetZoom]);

  // Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Prevent text selection (optional, for extra security)
  const handleSelectStart = (e) => {
    // Allow text selection but prevent copying (handled by CSS)
    // e.preventDefault();
  };

  // Note: Don't return early for loading state - let Document component render
  // The Document component has its own loading prop that handles the loading UI

  // Retry loading the PDF
  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setNumPages(null);
    setRetryCount(prev => prev + 1);
  };

  // Check if error is likely CORS-related for external URL
  const isExternalCorsError = isExternalUrl(fileUrl) && error;

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-neutral-900 p-4">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-semibold text-white mb-2">Failed to Load PDF</h3>
          <p className="text-neutral-400 mb-4">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {/* For external URLs, show Open in New Tab as primary action */}
            {fileUrl && isExternalCorsError && (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
              >
                Open in New Tab
              </a>
            )}
            <button
              onClick={handleRetry}
              className={`px-4 py-2 ${isExternalCorsError ? 'bg-neutral-700 hover:bg-neutral-600' : 'bg-primary-600 hover:bg-primary-700'} text-white rounded-lg transition-colors`}
            >
              Try Again
            </button>
            {/* For same-origin URLs, show Open in New Tab as secondary */}
            {fileUrl && !isExternalCorsError && (
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg transition-colors"
              >
                Open in New Tab
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`pdf-viewer ${isFullscreen ? 'fixed inset-0 z-50' : 'relative'} flex flex-col bg-neutral-900 h-full`}
      onContextMenu={handleContextMenu}
      onSelectStart={handleSelectStart}
      style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}
    >
      {/* Top Toolbar - Simplified for mobile */}
      <div className="bg-neutral-800 border-b border-neutral-700 px-2 sm:px-4 py-2 sm:py-3 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          {/* Title - hidden on mobile to save space */}
          <div className="hidden sm:flex items-center gap-4">
            <h2 className="text-white font-semibold truncate max-w-md">{fileName}</h2>
          </div>

          {/* Mobile: Compact page indicator */}
          <Badge variant="neutral" size="sm" className="sm:hidden">
            {currentPage} / {numPages}
          </Badge>

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Zoom Controls - simplified on mobile */}
            <div className="hidden sm:flex items-center gap-1 bg-neutral-700 rounded-lg p-1">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="p-2 text-neutral-300 hover:text-white hover:bg-neutral-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom Out (Ctrl+-)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <button
                onClick={handleResetZoom}
                className="px-3 py-2 text-sm font-medium text-neutral-300 hover:text-white hover:bg-neutral-600 rounded transition-colors min-w-[70px]"
                title="Reset Zoom (Ctrl+0)"
              >
                {Math.round(scale * 100)}%
              </button>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 3.0}
                className="p-2 text-neutral-300 hover:text-white hover:bg-neutral-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom In (Ctrl++)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={handleFitToWidth}
                className="p-2 text-neutral-300 hover:text-white hover:bg-neutral-600 rounded transition-colors"
                title="Fit to Width"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>
            </div>

            {/* Mobile zoom buttons */}
            <div className="flex sm:hidden items-center gap-1 bg-neutral-700 rounded-lg p-1">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="p-1.5 text-neutral-300 hover:text-white rounded transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              <span className="text-xs text-neutral-300 px-1">{Math.round(scale * 100)}%</span>
              <button
                onClick={handleZoomIn}
                disabled={scale >= 3.0}
                className="p-1.5 text-neutral-300 hover:text-white rounded transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>

            {/* Thumbnails Toggle - hidden on mobile */}
            {showThumbnails && (
              <button
                onClick={() => setShowThumbnailSidebar(!showThumbnailSidebar)}
                className={`hidden sm:block p-2 rounded transition-colors ${
                  showThumbnailSidebar ? 'bg-primary-600 text-white' : 'text-neutral-300 hover:text-white hover:bg-neutral-700'
                }`}
                title="Toggle Thumbnails (T)"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
            )}

            {/* Fullscreen Toggle */}
            {enableFullscreen && (
              <button
                onClick={toggleFullscreen}
                className="p-1.5 sm:p-2 text-neutral-300 hover:text-white hover:bg-neutral-700 rounded transition-colors"
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen (F)'}
              >
                {isFullscreen ? (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Thumbnail Sidebar */}
        <AnimatePresence>
          {showThumbnailSidebar && (
            <motion.div
              initial={{ x: -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-64 bg-neutral-800 border-r border-neutral-700 overflow-y-auto flex-shrink-0"
            >
              <div className="p-4">
                <h3 className="text-sm font-semibold text-white mb-3">Pages</h3>
                <div className="space-y-2">
                  {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-full p-2 rounded-lg border-2 transition-all ${
                        currentPage === pageNum
                          ? 'border-primary-500 bg-primary-900/30'
                          : 'border-neutral-600 hover:border-neutral-500 bg-neutral-700/50'
                      }`}
                    >
                      <div className="relative">
                        <div className="bg-white rounded overflow-hidden" style={{ aspectRatio: '8.5/11' }}>
                          <Document key={`thumb-${retryCount}`} file={pdfFile} loading={null}>
                            <Page
                              pageNumber={pageNum}
                              width={200}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                            />
                          </Document>
                        </div>
                        <p className="text-xs text-neutral-300 text-center mt-1">Page {pageNum}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Document Display Area — continuous scroll: all pages stacked vertically,
            user scrolls naturally like a native mobile PDF viewer. */}
        <div ref={documentAreaRef} className="flex-1 overflow-auto bg-neutral-800">
          <div className="flex flex-col items-center py-4 sm:py-8 px-2 sm:px-4 min-h-full gap-4">
            <Document
              key={`pdf-doc-${retryCount}`}
              file={pdfFile}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
                  <p className="text-white">Loading document...</p>
                </div>
              }
              className="pdf-document flex flex-col items-center gap-4"
            >
              {numPages && Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
                <div
                  key={`page-${pageNum}`}
                  ref={(el) => { pageRefs.current[pageNum] = el; }}
                  data-page-number={pageNum}
                  className="relative shadow-2xl bg-white"
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: 'center top',
                    transition: 'transform 0.2s ease-out',
                    // Leave vertical space equal to the scaled-up page so neighbours
                    // don't overlap when zoomed in.
                    marginBottom: scale > 1 ? `${(scale - 1) * containerWidth * 1.3}px` : undefined,
                  }}
                >
                  <Page
                    pageNumber={pageNum}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    width={containerWidth}
                    loading={
                      <div className="flex items-center justify-center" style={{ height: containerWidth * 1.3, width: containerWidth }}>
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                      </div>
                    }
                  />

                  {/* Watermark per page */}
                  {watermarkText && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <p className="text-6xl font-bold text-neutral-900 opacity-5 rotate-45 select-none">
                        {watermarkText}
                      </p>
                    </div>
                  )}

                  {/* Small page number badge bottom-right */}
                  <div className="absolute bottom-2 right-2 bg-neutral-900/70 text-white text-xs px-2 py-0.5 rounded pointer-events-none">
                    {pageNum} / {numPages}
                  </div>
                </div>
              ))}
            </Document>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar - Mobile optimized */}
      <div className="bg-neutral-700 border-t border-neutral-600 px-2 sm:px-4 py-2 sm:py-3 flex-shrink-0">
        <div className="flex items-center justify-center gap-2 sm:gap-4">
          {/* First page - hidden on mobile */}
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="hidden sm:block p-2 text-neutral-300 hover:text-white hover:bg-neutral-600 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="First Page (Home)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 sm:px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
            title="Previous Page (←)"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-1 sm:gap-2">
            <input
              type="number"
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (!isNaN(page)) {
                  handlePageChange(page);
                }
              }}
              min="1"
              max={numPages}
              className="w-12 sm:w-16 px-2 sm:px-3 py-1.5 sm:py-2 bg-neutral-600 text-white text-center rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm sm:text-base"
            />
            <span className="text-neutral-300 text-sm sm:text-base">/ {numPages}</span>
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === numPages}
            className="px-3 sm:px-4 py-2 bg-neutral-600 text-white rounded-lg hover:bg-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
            title="Next Page (→)"
          >
            <span className="hidden sm:inline">Next</span>
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Last page - hidden on mobile */}
          <button
            onClick={() => handlePageChange(numPages)}
            disabled={currentPage === numPages}
            className="hidden sm:block p-2 text-neutral-300 hover:text-white hover:bg-neutral-600 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Last Page (End)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Keyboard Shortcuts Hint - hidden on mobile */}
        <div className="hidden sm:block mt-2 text-center">
          <p className="text-xs text-neutral-400">
            Shortcuts: ← → (Navigate) | Ctrl +/- (Zoom) | T (Thumbnails) | F (Fullscreen)
          </p>
        </div>
      </div>

      {/* Print Blocking CSS */}
      <style>{`
        @media print {
          .pdf-viewer {
            display: none !important;
          }
        }
        .react-pdf__Page__textContent {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
        }
        .react-pdf__Page__canvas {
          pointer-events: none !important;
        }
      `}</style>
    </div>
  );
};

export default PDFViewer;
